import { Typography } from '@euler/app/components/typography';
import { CardLoading } from '@euler/app/flows/ai-suite/components/ChatCard';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { BottomPanel } from '@euler/components/BottomPanel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  BiDatasetPermission,
  BiDatasetVisibility,
  BiUploadedDatasetView,
} from '@euler/model/bi/dataset';
import {
  BiDatasetAudience,
  BiDatasetGrantInput,
  kBiMaxGrantsPerDataset,
} from '@euler/model/bi/library';
import { Lock, Users } from 'lucide-react-native';
import { memo, useEffect, useMemo, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { BiButton } from '../../../components/ui/BiButton';
import { BiRetry } from '../../../components/ui/BiRetry';
import { MemberPicker } from '../../../dashboard/sharing/MemberPicker';
import { VisibilityOption } from '../../../dashboard/sharing/VisibilityOption';
import { biErrorMessage } from '../../../functions/biErrors';
import { DatasetAudienceConfirm } from './DatasetAudienceConfirm';
import { DatasetGrantRow } from './DatasetGrantRow';
import { biDatasetAudienceLines } from './functions/biDatasetAudienceLines';
import { biSameDatasetGrants } from './functions/biSameDatasetGrants';
import { useBiDatasetSharing } from './functions/useBiDatasetSharing';

/**
 * Sharing an uploaded dataset, for a `manage` holder. Nothing is shared by
 * default; every widening first shows who it reaches, as the server
 * counts them, and applies only once confirmed.
 */
export const DatasetSharingPanel = memo(
  ({
    visible,
    dataset,
    onClose,
    onSaved,
  }: {
    visible: boolean;
    dataset: BiUploadedDatasetView;
    onClose: () => void;
    onSaved: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { state, retry, save } = useBiDatasetSharing(
      dataset.id,
      dataset.visibility,
      visible,
    );
    const [visibility, setVisibility] = useState(dataset.visibility);
    const [grants, setGrants] = useState<BiDatasetGrantInput[]>([]);
    const [audience, setAudience] = useState<BiDatasetAudience | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const base = state.kind === 'ready' ? state : null;
    useEffect(() => {
      if (!visible || !base) return;
      setVisibility(base.visibility);
      setGrants(base.grants);
      setAudience(null);
      setError(null);
    }, [visible, base]);

    const members = useMemo(
      () => (state.kind === 'ready' ? state.members : []),
      [state],
    );
    const nameOf = (memberId: number) =>
      members.find(member => member.id === memberId)?.name ??
      `成员 ${memberId}`;
    const grantable = useMemo(
      () =>
        members.filter(
          member =>
            member.id !== dataset.ownerMemberId &&
            !grants.some(grant => grant.memberId === member.id),
        ),
      [members, grants, dataset.ownerMemberId],
    );
    const changed =
      !!base &&
      (visibility !== base.visibility ||
        !biSameDatasetGrants(base.grants, grants));

    const submit = async (confirmed: boolean) => {
      if (!base) return;
      setBusy(true);
      setError(null);
      try {
        const outcome = await save(
          { visibility: base.visibility, grants: base.grants },
          { visibility, grants },
          confirmed,
        );
        if (outcome.kind === 'confirm') {
          setAudience(outcome.audience);
          if (outcome.partly) onSaved();
          return;
        }
        onSaved();
        onClose();
      } catch (e) {
        console.error('BI dataset sharing save failed', dataset.id, e);
        setError(biErrorMessage(e, '没有保存成功，请稍后重试。'));
        // A narrowing may have landed even when the rest failed.
        onSaved();
      } finally {
        setBusy(false);
      }
    };

    return (
      <BottomPanel visible={visible} onClose={onClose} tall>
        <View style={{ flex: 1, padding: rpx(24), gap: rpx(12) }}>
          <Typography.Text medium size={rpx(15)} color={aiColors.foreground}>
            共享数据
          </Typography.Text>
          {state.kind === 'loading' ? (
            <CardLoading label="正在读取共享设置…" />
          ) : state.kind === 'failed' ? (
            <BiRetry onRetry={retry} />
          ) : audience ? (
            <DatasetAudienceConfirm
              lines={biDatasetAudienceLines(audience)}
              error={error}
              busy={busy}
              onBack={() => setAudience(null)}
              onConfirm={() => void submit(true)}
            />
          ) : (
            <>
              <ScrollView
                style={{ flex: 1 }}
                contentContainerStyle={{ gap: rpx(12) }}
                keyboardShouldPersistTaps="handled"
              >
                <VisibilityOption
                  icon={Lock}
                  title="私有"
                  detail="只有你和下面添加的成员能使用"
                  selected={visibility === BiDatasetVisibility.Private}
                  onPress={() => setVisibility(BiDatasetVisibility.Private)}
                />
                <VisibilityOption
                  icon={Users}
                  title="门店可用"
                  detail="本店所有成员都能预览和使用；要让同事管理，在下面添加"
                  selected={visibility === BiDatasetVisibility.Store}
                  onPress={() => setVisibility(BiDatasetVisibility.Store)}
                />
                <Typography.Text
                  medium
                  size={rpx(13)}
                  color={aiColors.foreground}
                >
                  指定成员
                </Typography.Text>
                {grants.map(grant => (
                  <DatasetGrantRow
                    key={grant.memberId}
                    name={nameOf(grant.memberId)}
                    permission={grant.permission}
                    onChange={permission =>
                      setGrants(current =>
                        current.map(entry =>
                          entry.memberId === grant.memberId
                            ? { memberId: entry.memberId, permission }
                            : entry,
                        ),
                      )
                    }
                    onRemove={() =>
                      setGrants(current =>
                        current.filter(
                          entry => entry.memberId !== grant.memberId,
                        ),
                      )
                    }
                  />
                ))}
                {grants.length < kBiMaxGrantsPerDataset ? (
                  <MemberPicker
                    members={grantable}
                    onPick={member =>
                      setGrants(current => [
                        ...current,
                        {
                          memberId: member.id,
                          permission: BiDatasetPermission.Use,
                        },
                      ])
                    }
                  />
                ) : null}
              </ScrollView>
              {error ? (
                <Typography.Text size={rpx(12)} color={aiColors.destructive}>
                  {error}
                </Typography.Text>
              ) : null}
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  gap: rpx(8),
                }}
              >
                <BiButton title="取消" onPress={onClose} />
                <BiButton
                  title="保存"
                  tone="primary"
                  loading={busy}
                  disabled={!changed}
                  onPress={() => void submit(false)}
                />
              </View>
            </>
          )}
        </View>
      </BottomPanel>
    );
  },
);
